import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const COI_DEFAULT_TEXT =
  "Dichiaro, ai sensi della ISO/IEC 17021-1 e della ISO/IEC 17029, di non avere conflitti di interesse " +
  "nei confronti dell'organizzazione oggetto di valutazione (rapporti di consulenza, commerciali, familiari " +
  "o di dipendenza negli ultimi 2 anni) e di impegnarmi a comunicare tempestivamente qualsiasi situazione sopravvenuta.";

/** Conflict-of-interest declaration (checkbox + server-side timestamp). */
export default function CoiDialog({ item, text, onClose, onDone }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      await api.post(`/assessments/${item.assessment_id}/team/coi`, { slot: item.slot, confirmed: true });
      toast.success("Dichiarazione confermata e registrata nel log");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="coi-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-viva-600" /> Dichiarazione di assenza di conflitti di interesse
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Funzione: <b>{item.slot_label}</b>
              {item.organization_name && <> · Azienda: <b>{item.organization_name}</b></>}
              {item.year && <> · Anno {item.year}</>}
            </div>
            <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed" data-testid="coi-text">
              {text || COI_DEFAULT_TEXT}
            </p>
            <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
              <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="coi-checkbox" className="mt-0.5" />
              Confermo la dichiarazione. Data e ora verranno registrate nell&apos;audit log.
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="coi-cancel">Annulla</Button>
          <Button onClick={confirm} disabled={!checked || busy} data-testid="coi-confirm"
                  className="bg-viva-500 hover:bg-viva-600 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma dichiarazione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
